import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import CheckIcon from '../../svgs/CheckIcon';
import { useOutsideAlerter } from '../transcript/useClickOutside';

type Props = {
  values: { caption: string; value: string | null }[];
  onChange: (value: string | null) => void;
  defaultValue: string | undefined | null;
  value: string;
};

const PropertyDropdown = (props: Props) => {
  const [selectedItem, setSelectedItem] = useState<string | undefined | null>(
    props.defaultValue,
  );
  const [showDropdown, toggleDropdown] = useState<boolean>(false);

  useEffect(() => {
    setSelectedItem(props.defaultValue);
  }, [props.defaultValue]);

  useEffect(() => {
    if (showDropdown) {
      dropdownRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [showDropdown]);

  const contentRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(contentRef, () => {
    toggleDropdown(false);
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const ArrowDownIcon = (
    <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
      <path d="M2 4.33008L6 8.33008L10 4.33008" stroke="var(--accent)" />
    </svg>
  );

  const selectedValue = props.values.find(v => v.value === selectedItem);

  const renderDropdownItem = (item: {
    caption: string;
    value: string | null;
  }) => (
    <DropdownItem
      key={item.value}
      onClick={() => {
        setSelectedItem(item.value);
        toggleDropdown(false);
        props.onChange(item.value);
      }}
    >
      <DropdownTitle>{item.caption}</DropdownTitle>
      {selectedItem === item.value && (
        <CircleCheck>
          <CheckIcon />
        </CircleCheck>
      )}
    </DropdownItem>
  );

  return (
    <Main ref={contentRef}>
      <Selected
        onClick={() => {
          toggleDropdown(!showDropdown);
        }}
      >
        <SelectedText>{selectedValue?.caption || ''}</SelectedText>{' '}
        <ToggleIcon>{ArrowDownIcon}</ToggleIcon>
      </Selected>
      {showDropdown && (
        <Dropdown ref={dropdownRef}>
          {props.values.map(item => renderDropdownItem(item))}
        </Dropdown>
      )}
    </Main>
  );
};

export default PropertyDropdown;

const Main = styled.div`
  position: relative;
`;
const Selected = styled.div`
  border: 1px solid var(--border);
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
  cursor: pointer;

  padding: 4px 8px;
  border-radius: 8px;
`;

const SelectedText = styled.span`
  font-size: var(--text-sm);
  font-family: var(--font-primary);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow-y: hidden;
`;
const Dropdown = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);
  box-shadow: var(--elevation-toast);
  padding: 5px 0;
  max-height: 230px;
  width: 100%;
  overflow: auto;
  position: absolute;
  margin-top: 1px;

  cursor: pointer;
  z-index: 10;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const DropdownItem = styled.div`
  padding: 5px 16px;
  display: flex;
  align-items: center;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  &:hover {
    color: var(--accent);
  }
`;

const ToggleIcon = styled.button`
  border: 0;
  outline: 0;
  background-color: transparent;
`;
const DropdownTitle = styled.span`
  font-family: var(--font-primary);
  display: flex;
  flex: 0.9;
`;

const CircleCheck = styled.span`
  background-color: var(--primary);
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: flex;
  flex: 0.1

  color: var(--foreground);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
`;
